import { Injectable } from "@angular/core";
import { Subject, Subscription, SubscriptionLike, BehaviorSubject, Observable } from "rxjs";
import { filter, pairwise, map } from "rxjs/operators";
import { Router, RoutesRecognized, NavigationEnd, ActivatedRoute, Data, NavigationStart, ActivationEnd } from "@angular/router";
import { CoreLogService } from "./log.service";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { SnackBarMessageType, PopStateDirection, YourAPPProductType } from "../shared/contentTypes";
import { SnackBarService, snackBarMessageKey } from "./snackBar.service";
import { Location } from "@angular/common";
import { CoreCOMService } from "./com.service";
import { ClientType } from "./com/comEnvironmentClient";
import { NavigationService } from "./navigation.service";
import { ScrollHelper } from "../shared/scrollHelper";
import * as _ from "lodash";
import { environment } from "../../../environments/environment";

@Injectable({
  providedIn: "root"
})
export class RouterHelperService {
  // resolve data for next path
  private routeTargetData: Data = null;
  private _previousUrl: string = undefined;
  private _previousPopStateDirection: PopStateDirection;
  private _popStateDirection: PopStateDirection;
  private _locationPopState: boolean = false;
  private mainFooterHideBehaviorSubject = new BehaviorSubject<boolean>(false);
  private showCookiebannerBehaviorSubject = new BehaviorSubject<boolean>(false);
  private checkMainLayerHeightSubject = new Subject<void>();
  private pageTitleBehaviorSubject = new BehaviorSubject("");
  private locationPopStateSubject = new BehaviorSubject<boolean>(false);
  private _highLightNavigationSubject = new BehaviorSubject<number>(-1);
  private _locationSubscription: SubscriptionLike;
  private _checkMainLayerHeightScription: Subscription;
  private _moduleIntervalArray: NodeJS.Timer[] = [];
  private _initialQueryParams: Object;
  private _lastChildRouteDataSubject = new BehaviorSubject<any>({});
  private _leaveQRCodePageBehavierSubject = new Subject<void>();

  public get PageTitleObserver(): Observable<string> {
    return this.pageTitleBehaviorSubject;
  }

  public get MainFooterObserver(): Observable<boolean> {
    return this.mainFooterHideBehaviorSubject.asObservable();
  }

  public get ShowCookiebannerObserver(): Observable<boolean> {
    return this.showCookiebannerBehaviorSubject.asObservable();
  }

  public get LocationPopStateObserver(): Observable<boolean> {
    return this.locationPopStateSubject.asObservable();
  }

  public get PopStateDirection(): PopStateDirection {
    return this._popStateDirection;
  }

  public get HighLightNavigationObservable(): Observable<number> {
    return this._highLightNavigationSubject.asObservable();
  }
  public get LastChildRouteDataObservable(): Observable<number> {
    return this._lastChildRouteDataSubject.asObservable();
  }
  public get LeaveQRCodePageBehavierSubject(): Observable<void> {
    return this._leaveQRCodePageBehavierSubject.asObservable();
  }

  constructor(private _router: Router
    , private _coreLogService: CoreLogService
    , private _objObsInjectHelper: CircularInjectionHelper
    , private _snackBarService: SnackBarService
    , private _location: Location
    , private _activatedRoute: ActivatedRoute
    , private _coreCOMService: CoreCOMService
    , private _scrollHelper: ScrollHelper) {

    this._checkMainLayerHeightScription = this.checkMainLayerHeightSubject.subscribe(() => {
      const me = this;
      setTimeout(() => {
        const vh = +(document.documentElement.style.getPropertyValue("--vh").replace("px", ""));
        const scrollElement: HTMLElement = me._scrollHelper.getScrollElement()
        const scrollEnforcer: HTMLElement = document.getElementById("scroll-enforcer");
        if (scrollEnforcer) {
          const isIOS = me._coreCOMService.environment.client.isMobile && me._coreCOMService.environment.client.type === ClientType.ios;
          if (!isIOS) { // IOS has a static Footer
            if (scrollEnforcer.style.minHeight) {
              scrollEnforcer.style.removeProperty("min-Height");
            }
            const scrollElementScrollHeight = scrollElement.scrollHeight;
            const scrollElementClientHeight = scrollElement.clientHeight;
            const resolveData = me.getRouterData();
            if (resolveData != null) {
              // if a part of content is under the footer we add extra padding to be able to scroll
              if (scrollElementScrollHeight <= scrollElementClientHeight && !resolveData.value["hideFooter"]) {
                scrollEnforcer.style.minHeight = `${vh * 9}px`; // Height of Footer Element
              }
            } else {
              me._coreLogService.error("RouterHelperService.constructor", "_checkMainLayerHeightScription > getRouterData return null");
            }
          }
        }
      }, 500);
    });

    // TODO
    // to check if it is not back button don't scroll up
    this._locationSubscription = this._location.subscribe(
      (val: PopStateEvent) => {
        this._locationPopState = true;
      },
      (e) => {
        this._coreLogService.error("RouterHelperService.constructor", "location.subscribe", e);
      });

    // subscriber for popstate event
    this._router.events.pipe(
      filter((event: NavigationStart) => event instanceof NavigationStart),
      pairwise(),
      map(([event1, event2]) => {
        if (event2.navigationTrigger === "popstate" && event2.restoredState) {
          if (event1.navigationTrigger === "popstate" && event1.restoredState) {
            if (event1.restoredState.navigationId < event2.restoredState.navigationId) {
              if (this._previousPopStateDirection === PopStateDirection.Backward) {
                this._previousPopStateDirection = PopStateDirection.Forward;
              } else {
                this._previousPopStateDirection = PopStateDirection.Backward;
              }
            }
          } else {
            this._previousPopStateDirection = PopStateDirection.Backward;
          }
        } else {
          this._previousPopStateDirection = PopStateDirection.Imprative;
        }
        return this._previousPopStateDirection;
      })
    ).subscribe(direction => {
      this._popStateDirection = direction;
    });

    // redirect sot on root to sot (so werden die Query Parameter gelesen)
    if (environment.producttype === YourAPPProductType.sot) {
      this._router.events.pipe(
        filter(event => event instanceof NavigationStart)
      ).subscribe((event: NavigationStart) => {
        if (event.url === "/") {
          this._router.navigateByUrl("/sot");
        }
      });
    }

    // subscriber for RoutesRecognized Event
    this._router.events.pipe(
      filter(event => event instanceof RoutesRecognized)
    ).subscribe((event: RoutesRecognized) => {
      this._previousUrl = this._router.url;
      // handle navigate back on registrationdata; User must be signouted.
      if (this._router.url === "/registrationdata" && event.url === "/loginactivation") {
        this._objObsInjectHelper.yourAppServer.Auth.SignOut()
          .then((isSignOut) => {
            // Clear userData in yourAppServer.Auth and RegisterData in HelperService
          })
          .catch((e) => {
            this._snackBarService.ShowErrorMessageFailed(114);
          });
        this._router.navigateByUrl("/");
      } else {
        // Do nothink
      }
    });

    // subscriber for NavigationEnd Event
    this._router.events.pipe(
      filter(event => event instanceof NavigationStart)
    ).subscribe((event: NavigationStart) => {
      if (this._router.url === "/qrcodescanner") {
        this._leaveQRCodePageBehavierSubject.next();
      }
    });

    // subscriber for NavigationEnd Event
    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.CHeckMainLayerHeight();
      this.setScrollObserver();
      this.toggleFooter();
      this.toggleCookiebanner();
      this.setGlobalClasses();
      this.setRouteData(event);
      this.clearIntervals();
      const resolveData = this.getRouterData();
      if (resolveData != null) {
        this._lastChildRouteDataSubject.next(this.getRouterData().value);
        this._highLightNavigationSubject.next(this.getRouterData().value["highlightContentType"]);
      } else {
        this._coreLogService.error("RouterHelperService.constructor", "_router.events NavigationEnd > getRouterData return null");
      }
    });

    this.setupInitialQueryParamsHandling();
  }

  private clearIntervals(): void {
    const me = this;
    me._moduleIntervalArray.forEach(interval => {
      clearInterval(interval);
    });
  }

  public AddInterval(interval: NodeJS.Timer) {
    const me = this;
    me._moduleIntervalArray.push(interval);
  }

  /**
   * get the route data that is set by routing file or by setRouteTargetData() function
   * @result return resolver Data
   */
  public getRouterData(): Data {
    const me = this;
    let routeData: any = null;
    let router = me._activatedRoute.firstChild;
    if (router) {
      while (router.firstChild) {
        router = router.firstChild;
      }
      if (router.outlet === "primary") {
        routeData = router.data;
      }
    }
    return routeData;
  }

  /**
   * set the title of the page base on navigation data
   * @param event route navigation event thet includes current url
   */
  private setRouteData(event): void {
    const me = this;
    let router = me._activatedRoute.firstChild;
    if (router) {
      while (router.firstChild) {
        router = router.firstChild;
      }
      if (router.outlet === "primary") {
        const url = event.url === "/" ? "home" : event.url.replace("/", "");
        if (me.routeTargetData) {
          router.snapshot.data["routeTargetData"] = me.routeTargetData;
          me.resetRouteTargetData();
        }
      }
    }
  }

  /**
   * get the previous url on routing event
   */
  public getPreviousUrl() {
    return this._previousUrl;
  }

  /**
   * hide footer base on resolverData of routing
   */
  public hideFooter(): void {
    const me = this;
    me.mainFooterHideBehaviorSubject.next(true);
  }

  /**
   * show footer base on resolverData of routing
   */
  public showFooter(): void {
    const me = this;
    me.mainFooterHideBehaviorSubject.next(false);
  }

  /**
 * hide Cookiebanner base on resolverData of routing
 */
  public hideCookiebanner(): void {
    const me = this;
    me.showCookiebannerBehaviorSubject.next(false);
  }

  /**
 * show Cookiebanner base on resolverData of routing
 */
  public showCookiebanner(): void {
    const me = this;
    me.showCookiebannerBehaviorSubject.next(true);
  }

  /**
   * check if user use back button to navigation; if not page must scroll to top.
   */
  private setScrollObserver() {
    const me = this;
    me.locationPopStateSubject.next(me._locationPopState);
    me._locationPopState = false;
  }

  /**
   * hide and show footer base on hideFooter resolverdata on app.routing.ts
   */
  public toggleFooter(): void {
    const me = this;
    const resolveData = me.getRouterData();
    if (resolveData != null) {
      const vh = +(document.documentElement.style.getPropertyValue("--vh").replace("px", ""));
      if (resolveData.value["hideFooter"]) {
        me.hideFooter();
      } else {
        me.showFooter();
      }
    } else {
      me._coreLogService.error("RouterHelperService.toggleFooter", "getRouterData return null");
    }
  }

  public toggleCookiebanner(): void {
    const me = this;
    const resolveData = me.getRouterData();
    if (resolveData != null) {
      if (resolveData.value["showCookiebanner"] === false) {
        me.hideCookiebanner();
      } else {
        me.showCookiebanner();
      }
    } else {
      me._coreLogService.error("RouterHelperService.toggleCookiebanner", "getRouterData return null");
    }
  }

  public setGlobalClasses() {
    const me = this;
    const resolveData = me.getRouterData();
    if (resolveData != null) {
      if (resolveData.value["isOldDesignRoute"]) {
        document.documentElement.classList.add("olddesign");
      } else {
        document.documentElement.classList.remove("olddesign");
      }
    } else {
      me._coreLogService.error("RouterHelperService.setGlobalClasses", "getRouterData return null");
    }
  }

  /**
   * set route Data for next path
   * @param key key of object or Data
   * @param value value of Data
   */
  public setRouteTargetDataByKey(key: string, value: any): void {
    const me = this;
    if (!me.routeTargetData) {
      me.routeTargetData = {};
    }
    this.routeTargetData[key] = value;
  }

  /**
   * set an object as Data for next path
   * @param data object data of next path
   */
  public setRouteTargetData(data: any): void {
    this.routeTargetData = data;
  }

  // delete all resolver data
  private resetRouteTargetData(): void {
    const me = this;
    me.routeTargetData = null;
  }

  public CHeckMainLayerHeight() {
    const me = this;
    me.checkMainLayerHeightSubject.next();
  }

  /**
   * Stores the Query Parameters the APP was initially called with
   * @param queryParams object queryParams data
   */
  public setInitialQueryParameters(queryParams: object) {
    this._initialQueryParams = queryParams;
  }

  /**
   * gets the URL QueryParameters the APP was called with
   */
  public getInitialQueryParameters(): object {
    return this._initialQueryParams;
  }

  /**
   * Setups getting initialQueryParams and maintainingen through history navigation on Browser
   */
  private setupInitialQueryParamsHandling() {
    const me = this;
    if (!me._coreCOMService.environment.client.isMobile) {
      // Get and Save initialQueryParams
      const initialQueryParams = decodeURIComponent(window.location.search)
        .substring(1)
        .split("&")
        .map(param => param.split("="))
        .reduce((obj, [key, value]) => {
          obj[key] = value;
          return obj;
        }, {});
      this.setInitialQueryParameters(initialQueryParams);

      // Save initialQueryParameters in History so they can be restored on reload or browsernavigation
      this._router.events.pipe(
        filter(event => event instanceof ActivationEnd)
      ).subscribe(() => {
        const iqp = this.getInitialQueryParameters();
        if (iqp) {
          history.replaceState(_.extend({ iqp: iqp }, history.state), null);
        }
      });

      // Restore initialQueryParameters on History Navigation (Refresh or back and forth)
      window.addEventListener("popstate", () => {
        if (!_.isNil(history.state)) {
          const savedIQP = history.state["iqp"];
          if (savedIQP) {
            this.setInitialQueryParameters(savedIQP);
          }
        }
      });
    }
  }

  /**
   * Navigates Back to HomeMenu or optionally to a target route determined by the RouteData "simpleBackRoute"
   */
  public navigateBackSimple() {
    const me = this;
    const resolveData = me.getRouterData();
    if (resolveData != null) {
      const backroute: string = resolveData.value["simpleBackRoute"];
      if (backroute != null) {
        me._router.navigateByUrl(backroute);
      } else {
        me._location.back();
      }
    } else {
      me._coreLogService.error("RouterHelperService.navigateBackSimple", "getRouterData return null");
    }
  }
}
